import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, Circle, Users, Camera } from "lucide-react";
import { toast } from "sonner";
import TimothyCard from "@/components/TimothyCard";
import LeaderOnboarding from "@/components/LeaderOnboarding";
import LeadAGroupCTA from "@/components/LeadAGroupCTA";
import { NudgeBanner } from "@/components/NudgeBanner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MOVEMENT_COLOR = {
  Trust: "text-primary",
  Follow: "text-accent",
  Imitate: "text-[hsl(var(--chart-4))]",
  "Teach Others": "text-[hsl(var(--chart-5))]",
};

function useAuthedImage(url) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    if (!url) { setSrc(""); return; }
    let cancelled = false;
    let blobUrl = null;
    (async () => {
      try {
        const rel = url.replace(/^\/api/, "");
        const r = await api.get(rel, { responseType: "blob" });
        if (cancelled) return;
        blobUrl = URL.createObjectURL(r.data);
        setSrc(blobUrl);
      } catch {}
    })();
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [url]);
  return src;
}

export default function Weeks() {
  const { user, setUser } = useAuth();
  const [weeks, setWeeks] = useState([]);
  const [progress, setProgress] = useState({});
  const [groups, setGroups] = useState([]);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const avatarSrc = useAuthedImage(user?.picture_url);

  useEffect(() => {
    (async () => {
      const [w, p, g, s] = await Promise.all([
        api.get("/curriculum/weeks"),
        api.get("/progress"),
        api.get("/groups"),
        api.get("/assessment/status").catch(() => ({ data: null })),
      ]);
      setWeeks(w.data);
      const map = {};
      p.data.forEach((x) => (map[x.week_number] = x.completed));
      setProgress(map);
      setGroups(g.data);
      setAssessment(s.data);
    })();
  }, []);

  const completed = Object.values(progress).filter(Boolean).length;
  const pct = weeks.length ? Math.round((completed / weeks.length) * 100) : 0;

  const movements = ["Trust", "Follow", "Imitate", "Teach Others"];

  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5 MB)."); return; }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUser({ ...user, picture_url: data.url, picture_path: data.path });
      toast.success("Photo updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-14">
      <section className="reveal flex items-start justify-between gap-6">
        <div className="flex-1">
          <p className="text-xs uppercase tracking-[0.2em] text-primary">Peace be with you, {user?.name?.split(" ")[0]}</p>
          <h1 className="font-serif text-5xl mt-3 leading-tight text-balance">
            The eleven weeks. <em className="text-muted-foreground">Yours to walk.</em>
          </h1>
        </div>
        <div className="shrink-0">
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            data-testid="avatar-upload" title="Change photo"
            className="relative w-16 h-16 rounded-full overflow-hidden border border-border bg-secondary flex items-center justify-center group">
            {avatarSrc ? (
              <img src={avatarSrc} alt="Your photo" className="w-full h-full object-cover" />
            ) : (
              <span className="font-serif text-2xl text-muted-foreground">{user?.name?.[0]?.toUpperCase()}</span>
            )}
            <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
              <Camera className="w-4 h-4 text-white" />
            </span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={uploadAvatar} className="hidden" data-testid="avatar-file" />
        </div>
      </section>

      <TimothyCard />
      <LeaderOnboarding />
      <LeadAGroupCTA completedCount={completed} user={user} />

      {assessment && !assessment.start_submitted && <NudgeBanner />}

      {assessment && !assessment.start_submitted && (
        <section data-testid="assessment-banner-start"
          className="border border-primary/50 bg-primary/5 p-6 flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-xl">
            <div className="text-xs uppercase tracking-[0.2em] text-primary mb-1">Before Week 1</div>
            <p className="font-serif text-lg leading-relaxed">
              Take the Spiritual Health Assessment — a gentle mirror. In eleven weeks, we'll show you side-by-side what God has done.
            </p>
          </div>
          <Link to="/app/assessment/start"
            data-testid="assessment-banner-start-cta"
            className="inline-flex items-center rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-primary/90 transition-colors">
            {assessment.start_has_draft ? "Continue assessment" : "Begin assessment"}
          </Link>
        </section>
      )}

      {assessment && assessment.finish_unlocked && !assessment.finish_submitted && (
        <section data-testid="assessment-banner-finish"
          className="border border-accent/50 bg-accent/5 p-6 flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-xl">
            <div className="text-xs uppercase tracking-[0.2em] text-accent mb-1">Week 11 unlocked</div>
            <p className="font-serif text-lg leading-relaxed">
              Finishing Assessment is ready. Answer the same questions you did at the start — then see what has shifted.
            </p>
          </div>
          <Link to="/app/assessment/finish"
            data-testid="assessment-banner-finish-cta"
            className="inline-flex items-center rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-primary/90 transition-colors">
            {assessment.finish_has_draft ? "Continue" : "Begin finishing assessment"}
          </Link>
        </section>
      )}

      {assessment?.finish_submitted && (
        <section className="border border-border bg-card p-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Comparison</div>
            <p className="font-serif text-lg">Your Week 1 and Week 11 answers, side by side.</p>
          </div>
          <Link to="/app/assessment/compare" data-testid="assessment-view-compare"
            className="inline-flex items-center rounded-full border border-border px-5 py-2.5 text-sm hover:bg-secondary/40 transition-colors">
            Open the comparison
          </Link>
        </section>
      )}

      <section>
        <div className="mt-2 grid md:grid-cols-3 gap-6 items-end">
          <div className="md:col-span-2">
            <div className="flex justify-between text-xs uppercase tracking-widest text-muted-foreground mb-2">
              <span>Progress</span>
              <span data-testid="progress-text">{completed} of {weeks.length} weeks</span>
            </div>
            <Progress value={pct} data-testid="progress-bar" className="h-1" />
          </div>
          <div className="text-right text-sm text-muted-foreground">
            {user?.is_leader && (
              <Link to="/app/stats" className="text-primary underline-serif" data-testid="leader-stats-link">
                Community stats →
              </Link>
            )}
          </div>
        </div>
      </section>

      {movements.map((m, mi) => {
        const inMove = weeks.filter((w) => w.movement === m);
        if (!inMove.length) return null;
        return (
          <section key={m} className="reveal" style={{ animationDelay: `${mi * 60}ms` }}>
            <div className="flex items-baseline gap-4 mb-6">
              <span className="font-serif text-2xl text-primary tabular-nums">{String(mi + 1).padStart(2, "0")}</span>
              <h2 className={`font-serif text-3xl ${MOVEMENT_COLOR[m] || ""}`}>{m}</h2>
              <div className="flex-1 rule-line ml-2 mb-2"></div>
              <span className="text-xs text-muted-foreground">Weeks {inMove[0].week_number}–{inMove[inMove.length - 1].week_number}</span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {inMove.map((w) => (
                <Link key={w.week_number} to={`/app/weeks/${w.week_number}`}
                  data-testid={`week-card-${w.week_number}`}
                  className="group border border-border bg-card p-6 hover:border-primary/40 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Week {w.week_number}</div>
                    {progress[w.week_number] ? (
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                    ) : (
                      <Circle className="w-4 h-4 text-muted-foreground/50" />
                    )}
                  </div>
                  <div className="font-serif text-xl mt-3 leading-snug group-hover:text-primary transition-colors">
                    {w.title}
                  </div>
                  <div className="mt-4 text-xs text-muted-foreground italic">{w.memory_verse_ref}</div>
                </Link>
              ))}
            </div>
          </section>
        );
      })}

      {groups.length > 0 && (
        <section className="border-t border-border pt-10">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-serif text-2xl">Your groups</h2>
            <Link to="/app/groups" className="text-sm text-primary underline-serif" data-testid="see-all-groups">See all →</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {groups.slice(0, 3).map((g) => (
              <Link key={g.id} to={`/app/groups/${g.id}`}
                data-testid={`home-group-${g.id}`}
                className="border border-border bg-card p-5 hover:border-primary/40">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Users className="w-3 h-3" /> {g.member_count} members
                </div>
                <div className="font-serif text-lg mt-2">{g.name}</div>
                <div className="text-xs text-muted-foreground mt-1">Led by {g.leader_name}</div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
